import React, { useContext, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import IRMA from "@models/IRMA";
import IRMA_Line_Item from "@models/IRMA_Line_Item";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwTextArea from "@components/shared/OwTextArea/OwTextArea";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import { OwSelectOptionFn } from "@components/shared/OwSelectOptionFn/OwSelectOptionFn";
import RmaService from "@services/RmaService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import RmaConditionsOnReceiptEnum from "@models/RmaConditionsOnReceiptEnum";
import "./RecordInspectionModal.scss";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

type Props = {
    rma: IRMA;
    lineItem: IRMA_Line_Item | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

interface InspectionForm {
    condition_id: number | null;
    quantity: number;
    restockable: boolean;
    restock_warehouse_id: number | null;
    notes: string;
    refund_amount: number;
}

export const RecordInspectionModal = (props: Props) => {
    const { rma, lineItem, isOpen, onClose, onSuccess } = props;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const [inspections, setInspections] = useState<InspectionForm[]>([
        {
            condition_id: null,
            quantity: 0,
            restockable: false,
            restock_warehouse_id: null,
            notes: "",
            refund_amount: 0,
        },
    ]);
    const [loading, setLoading] = useState<boolean>(false);

    const conditionOptions = [
        { value: RmaConditionsOnReceiptEnum.GOOD, label: "good" },
        { value: RmaConditionsOnReceiptEnum.DAMAGED, label: "damaged" },
        { value: RmaConditionsOnReceiptEnum.UNUSABLE, label: "unusable" },
        { value: RmaConditionsOnReceiptEnum.NOT_RECEIVED, label: "not_received" },
    ];

    // Get warehouses from rma or context (simplified - you may want to fetch from API)
    const warehouseOptions = rma.Warehouse ? [{ value: rma.Warehouse.id, label: rma.Warehouse.name }] : [];

    useEffect(() => {
        if (isOpen) {
            // Reset form
            setInspections([
                {
                    condition_id: null,
                    quantity: 0,
                    restockable: false,
                    restock_warehouse_id: rma.warehouse_id,
                    notes: "",
                    refund_amount: 0,
                },
            ]);
        }
    }, [isOpen, rma.warehouse_id]);

    const handleAddInspection = () => {
        setInspections([
            ...inspections,
            {
                condition_id: null,
                quantity: 0,
                restockable: false,
                restock_warehouse_id: rma.warehouse_id,
                notes: "",
                refund_amount: 0,
            },
        ]);
    };

    const handleRemoveInspection = (index: number) => {
        if (inspections.length > 1) {
            setInspections(inspections.filter((_, i) => i !== index));
        }
    };

    const handleInspectionChange = (index: number, field: keyof InspectionForm, value: any) => {
        const updated = [...inspections];
        updated[index] = { ...updated[index], [field]: value };
        setInspections(updated);
    };

    const getTotalInspected = (): number => {
        return inspections.reduce((sum, insp) => sum + insp.quantity, 0);
    };

    const getTotalAlreadyInspected = (): number => {
        if (!lineItem || !lineItem.RMA_Line_Item_Inspections) return 0;
        return lineItem.RMA_Line_Item_Inspections.reduce((sum, insp) => sum + insp.quantity, 0);
    };

    const handleSubmit = async () => {
        if (!lineItem) return;

        // Validation
        for (let i = 0; i < inspections.length; i++) {
            const insp = inspections[i];
            if (!insp.condition_id) {
                uiContext.showNotification({
                    message: `Inspection ${i + 1}: Please select a condition`,
                    level: NotificationLevelEnum.error,
                });
                return;
            }
            if (insp.quantity <= 0) {
                uiContext.showNotification({
                    message: `Inspection ${i + 1}: Quantity must be greater than 0`,
                    level: NotificationLevelEnum.error,
                });
                return;
            }
            if (insp.restockable && !insp.restock_warehouse_id && supplierModel.has_warehouses) {
                uiContext.showNotification({
                    message: `Inspection ${i + 1}: Please select a warehouse for restockable items`,
                    level: NotificationLevelEnum.error,
                });
                return;
            }
        }

        try {
            setLoading(true);
            await RmaService.recordInspection(
                rma.guid,
                lineItem.guid,
                {
                    inspections: inspections.map((insp) => ({
                        condition_id: insp.condition_id!,
                        quantity: insp.quantity,
                        restockable: insp.restockable,
                        restock_warehouse_id: insp.restock_warehouse_id || undefined,
                        notes: insp.notes || undefined,
                        refund_amount: insp.refund_amount || undefined,
                    })),
                },
                userModel,
                supplierModel
            );

            uiContext.showNotification({
                message: "Inspection recorded successfully",
                level: NotificationLevelEnum.success,
            });
            onSuccess();
            onClose();
        } catch (err) {
            uiContext.showNotification({
                message: err instanceof Error ? err.message : "Failed to record inspection",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setLoading(false);
        }
    };

    if (!lineItem) return null;

    const qtyReceived = lineItem.qty_received || 0;
    const alreadyInspected = getTotalAlreadyInspected();
    const remaining = qtyReceived - alreadyInspected;
    const totalThisInspection = getTotalInspected();

    return (
        <Modal show={isOpen} onHide={onClose} size="xl" className="RecordInspectionModal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <OwLabel title="record_inspection" /> - {lineItem.sku} {lineItem.catalog_item_name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>
                        <OwLabel title="qty_received" />: {qtyReceived} | <OwLabel title="already_inspected" />: {alreadyInspected} |{" "}
                        <OwLabel title="remaining" />: {remaining}
                    </strong>
                </div>

                {inspections.map((insp, index) => (
                    <div key={index} className="inspection-form-group p-3 mb-3 border rounded">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6>
                                <OwLabel title="inspection" /> #{index + 1}
                            </h6>
                            {inspections.length > 1 && (
                                <OwButton title="remove" fn={() => handleRemoveInspection(index)} active={loading} />
                            )}
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <OwLabel title="condition" isBold={true} />
                                <OwSelectOptionFn
                                    name="condition_id"
                                    selectedOption={insp.condition_id}
                                    valueProp="value"
                                    titleProp="label"
                                    fn={(e: any) => handleInspectionChange(index, "condition_id", parseInt(e.target.value))}
                                    options={conditionOptions}
                                    hasSelectedOptionProp={false}
                                    translateTitle={true}
                                />
                            </div>
                            <div className="col-md-6">
                                <OwLabel title="quantity" isBold={true} />
                                <OwInputWrapper
                                    name="quantity"
                                    type="number"
                                    value={insp.quantity.toString()}
                                    fn={(e: any) => handleInspectionChange(index, "quantity", parseInt(e.target.value) || 0)}
                                    min={0}
                                    max={qtyReceived - alreadyInspected}
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            {insp.condition_id === RmaConditionsOnReceiptEnum.GOOD ||
                            insp.condition_id === RmaConditionsOnReceiptEnum.DAMAGED ? (
                                <div className="col-md-6">
                                    <OwCheckbox
                                        label="restockable"
                                        checked={insp.restockable}
                                        fn={(e) => handleInspectionChange(index, "restockable", e.target.checked)}
                                    />
                                </div>
                            ) : null}
                            {insp.restockable && supplierModel.has_warehouses && (
                                <div className="col-md-6">
                                    <OwLabel title="warehouse" isBold={true} />
                                    <OwSelectOptionFn
                                        name="restock_warehouse_id"
                                        selectedOption={insp.restock_warehouse_id}
                                        valueProp="id"
                                        titleProp="name"
                                        fn={(e: any) => handleInspectionChange(index, "restock_warehouse_id", parseInt(e.target.value))}
                                        options={warehouseOptions}
                                        hasSelectedOptionProp={false}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-6">
                                <OwLabel title="refund_amount" isBold={true} />
                                <OwInputWrapper
                                    name="refund_amount"
                                    type="number"
                                    step={0.01}
                                    value={insp.refund_amount.toString()}
                                    fn={(e: any) => handleInspectionChange(index, "refund_amount", parseFloat(e.target.value) || 0)}
                                    min={0}
                                />
                            </div>
                            <div className="col-md-6">
                                <OwLabel title="notes" isBold={true} />
                                <OwTextArea
                                    name="notes"
                                    value={insp.notes}
                                    fn={(e) => handleInspectionChange(index, "notes", e.target.value)}
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>
                ))}

                <OwButton title="add_another_condition" fn={handleAddInspection} active={loading} className="btn-secondary mb-3" />

                <div className="mt-3">
                    <strong>
                        <OwLabel title="total_this_inspection" />: {totalThisInspection}
                    </strong>
                    <br />
                    <strong>
                        <OwLabel title="total_all_inspections" />: {alreadyInspected + totalThisInspection}
                    </strong>
                    {alreadyInspected + totalThisInspection === qtyReceived && (
                        <OwLabel className="ms-2 text-success" title="matches_qty_received" leftIconClass="fas fa-check" />
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <OwButton title="cancel" fn={onClose} disabled={loading} />
                <OwButton title="record_inspection" fn={handleSubmit} active={loading} />
            </Modal.Footer>
        </Modal>
    );
};
